/**
 * AP-773 — history commit points for the worksheet's free text (§3.1 A6 / A7).
 *
 * - Finding rows commit once per gesture (blur / Enter / ✕ / picker); a
 *   statement added or removed is an A6, changed text is an A7.
 * - The conclusion editor reports every debounced update; the step is
 *   recorded on blur, against the text the editor had at focus.
 *
 * Refs mirror the state so the handlers compare against the last committed
 * value without being re-created on every keystroke.
 */
import {
  useCallback,
  useEffect,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { FindingStatement } from '@/models/report';
import { statementSignature } from '@/features/report/Findings/FindingInput/utils/statements';
import { commitHistoryStep } from '@/features/study-detail/history/historyStore';
import { createWorksheetStep } from '@/features/study-detail/history/worksheetSteps';
import {
  getWorksheetSnapshot,
  type ConclusionState,
} from '@/features/study-detail/history/worksheetApplier';

interface Args {
  findings: Record<string, FindingStatement[]>;
  setFindings: Dispatch<SetStateAction<Record<string, FindingStatement[]>>>;
  conclusion: ConclusionState | null;
  setConclusion: Dispatch<SetStateAction<ConclusionState | null>>;
}

export interface WorksheetTextHandlers {
  handleFindingChange: (
    findingClass: string,
    value: FindingStatement[]
  ) => void;
  handleConclusionChange: (value: string) => void;
  handleConclusionFocus: () => void;
  handleConclusionBlur: () => void;
}

export function useWorksheetTextHistory({
  findings,
  setFindings,
  conclusion,
  setConclusion,
}: Args): WorksheetTextHandlers {
  const findingsRef = useRef(findings);
  const conclusionRef = useRef(conclusion);
  useEffect(() => {
    findingsRef.current = findings;
  }, [findings]);
  useEffect(() => {
    conclusionRef.current = conclusion;
  }, [conclusion]);

  const handleFindingChange = useCallback(
    (findingClass: string, value: FindingStatement[]) => {
      const previous = findingsRef.current[findingClass] ?? [];
      if (statementSignature(previous) !== statementSignature(value)) {
        const before = getWorksheetSnapshot();
        if (before) {
          const kind = previous.length === value.length ? 'A7' : 'A6';
          commitHistoryStep(
            createWorksheetStep(
              kind,
              before,
              kind === 'A6' ? 'finding statement' : 'finding text'
            )
          );
        }
      }
      findingsRef.current = { ...findingsRef.current, [findingClass]: value };
      setFindings((f) => ({ ...f, [findingClass]: value }));
    },
    [setFindings]
  );

  const handleConclusionChange = useCallback(
    (value: string) => {
      conclusionRef.current = { uuid: conclusionRef.current?.uuid, value };
      setConclusion((prev) => ({ uuid: prev?.uuid, value }));
    },
    [setConclusion]
  );

  // Conclusion text at focus time; null while the editor is not focused.
  const baselineRef = useRef<string | null>(null);
  const handleConclusionFocus = useCallback(() => {
    baselineRef.current = conclusionRef.current?.value ?? '';
  }, []);
  const handleConclusionBlur = useCallback(() => {
    const baseline = baselineRef.current;
    baselineRef.current = null;
    if (baseline === null) return;
    if ((conclusionRef.current?.value ?? '') === baseline) return;
    const snapshot = getWorksheetSnapshot();
    if (!snapshot) return;
    commitHistoryStep(
      createWorksheetStep(
        'A7',
        {
          ...snapshot,
          conclusion: { uuid: conclusionRef.current?.uuid, value: baseline },
        },
        'conclusion text'
      )
    );
  }, []);

  return {
    handleFindingChange,
    handleConclusionChange,
    handleConclusionFocus,
    handleConclusionBlur,
  };
}
